import axios from 'axios';
import EventBus from 'eventing-bus';
import { setUserDetails } from './index';
import { all, takeEvery, call, put } from 'redux-saga/effects';

import { saveloginData } from '../Auth/index';
import { setUpdateWorkspace, setPendingInvites, setApiKeys, setApiKeyCreated } from './index';
import { setWorkspaceId } from '../axios';
import constants from '../../constants';

function* getBranding() {
    const { error, response } = yield call(getCall, '/user/branding');
    if (response) {
        const brandingData = response['data']['body'];
        yield put({ type: 'SET_DOMAIN_BRANDING', payload: brandingData });

        // Update constants with entities from backend
        if (brandingData.entities) {
            constants.objectsSchema = brandingData.entities;
        }
    } else {
        // Branding fetch failed — still mark as resolved so gated UI (animations) can render
        // with the default theme instead of waiting forever.
        yield put({ type: 'SET_BRANDING_LOADED' });
    }
}

function* getMe() {
    const { response } = yield call(getCall, '/user/myProfile');
    if (response) yield put(setUserDetails(response['data']['body']));
}

function* refreshSubscription() {
    const { response } = yield call(getCall, '/user/myProfile');
    if (response && response['data']['body']['workspace']?.['currentSubscription']) yield put({ type: 'UPDATE_CURRENT_SUBSCRIPTION', payload: response['data']['body']['workspace']['currentSubscription'] });
}

function* updateProfile({ payload }) {
    const { error, response } = yield call(putCall, { path: `/user/updateProfile`, payload });
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) yield put(setUserDetails(response['data']['body']));
}

function* updateWorkspace({ payload }) {
    const { error, response } = yield call(putCall, { path: `/user/workspace/${payload['_id']}`, payload });
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) yield put(setUpdateWorkspace(response['data']['body']));
}

function* createWorkspace({ payload }) {
    yield put({ type: 'SET_LOAD_BUTTON', payload: true });
    const { error, response } = yield call(postCall, { path: `/user/workspace`, payload });
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) {
        // Update user details with new workspace data
        yield put(setUserDetails(response['data']['body']));
        
        // Get the new workspace ID
        const newWorkspaceId = response['data']['body']['workspace']['_id'];
        
        // Update axios headers with new workspace ID
        setWorkspaceId(newWorkspaceId);
        
        // Navigate to the new workspace
        window.location.href = `/${newWorkspaceId}/agents`;
    }
    yield put({ type: 'SET_LOAD_BUTTON', payload: false });
}

function* switchWorkspace({ payload }) {
    yield put({ type: 'SET_LOAD_BUTTON', payload: true });
    const { error, response } = yield call(putCall, { path: `/user/workspace/${payload}/switch`, payload: {} });
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) {
        // Update user details with switched workspace data
        yield put(setUserDetails(response['data']['body']));
        
        // Update axios headers with new workspace ID
        setWorkspaceId(payload);

        // Navigate to the workspace root
        window.location.href = `/${payload}/analytics`;
    }
    yield put({ type: 'SET_LOAD_BUTTON', payload: false });
}

function* createMember({ payload }) {
    const { error, response } = yield call(postCall, { path: `/user/workspace/${payload['workspaceId']}/members`, payload });
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) {
        EventBus.publish('success', response['data']['message']);
        yield put(setUpdateWorkspace(response['data']['body']));
    }
}

function* updateMember({ payload }) {
    const { error, response } = yield call(putCall, { path: `/user/workspace/${payload['workspaceId']}/members/${payload['userId']}`, payload });
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) {
        yield call({ type: 'GET_ME' });
        yield put(setUpdateWorkspace(response['data']['body']));
    }
}

function* deleteMember({ payload }) {
    const { error, response } = yield call(deleteCall, `/user/workspace/${payload['workspaceId']}/members/${payload['userId']}`);
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) {
        EventBus.publish('success', response['data']['message']);
        yield put(setUpdateWorkspace(response['data']['body']));
        yield put({ type: 'GET_PENDING_INVITES', payload: payload['workspaceId'] });
    }
}

function* getPendingInvites({ payload }) {
    const { error, response } = yield call(getCall, `/user/workspace/${payload}/pendingInvites`);
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) yield put(setPendingInvites(response['data']['body']));
}

function* resendInvite({ payload }) {
    const { error, response } = yield call(postCall, { path: `/user/workspace/${payload['workspaceId']}/resendInvite`, payload });
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) EventBus.publish('success', response['data']['message']);
}

function* acceptInvite({ payload }) {
    const { error, response } = yield call(getCall, `/user/workspaceInvite/${payload['token']}`);
    if (error) {
        EventBus.publish('error', error['response']['data']['message']);
        payload['history'].push('/login');
    } else if (response) {
        EventBus.publish('success', response['data']['message']);
        yield put(saveloginData(response['data']['body']));
        yield put({ type: 'GET_ME' });
        setTimeout(() => payload['history'].push('/agents'), 500);
    }
}

// API Key Sagas
function* getApiKeys() {
    const { error, response } = yield call(getCall, '/user/apiKey');
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) yield put(setApiKeys(response['data']['body']));
}

function* createApiKey({ payload }) {
    const { error, response } = yield call(postCall, { path: '/user/apiKey', payload });
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) {
        EventBus.publish('success', 'API Key created successfully');
        yield put(setApiKeyCreated(response['data']['body']));
        yield call(getApiKeys); // Refresh the list
    }
}

function* deleteApiKey({ payload }) {
    const { error, response } = yield call(deleteCall, `/user/apiKey/${payload}`);
    if (error) EventBus.publish('error', error['response']['data']['message']);
    else if (response) yield call(getApiKeys); // Refresh the list
}

// Phone Verification Sagas
function* sendPhoneVerification({ payload }) {
    try {
        // First update the user profile with phone number
        const { error: updateError, response: updateResponse } = yield call(putCall, { path: '/user/updateProfile', payload: { phone: payload.phone }});
        if (updateError) return EventBus.publish('error', updateError['response']['data']['message']);

        // Now send SMS verification code
        const { error: smsError, response: smsResponse } = yield call(getCall, '/user/sendSMS');

        if (smsError) EventBus.publish('error', smsError['response']['data']['message']);
        else if (smsResponse) {
            const responseData = smsResponse['data']['body'];
            const requiresVerification = responseData?.requiresVerification;

            if (requiresVerification) {
                // US number: Show verification modal
                EventBus.publish('success', 'Verification code sent to your phone');
                if (payload.onSuccess) payload.onSuccess();
            } else {
                // Non-US number: Auto-verified, no modal needed
                EventBus.publish('success', smsResponse['data']['message']);
                // Refresh user details to update phoneVerified status
                yield put({ type: 'GET_ME' });
                // Call onComplete callback if provided (different from onSuccess)
                if (payload.onComplete) payload.onComplete();
            }
        }
    } catch (e) { EventBus.publish('error', 'Failed to send verification code') }
}

function* verifyPhoneCode({ payload }) {
    try {
        const { error, response } = yield call(postCall, { path: '/user/enableSMS', payload: { authKey: payload['code'] }});

        if (error) EventBus.publish('error', error['response']['data']['message']);
        else if (response) {
            EventBus.publish('success', 'Phone verified successfully!');
            // Refresh user details
            yield put({ type: 'GET_ME' });
            // Notify success via callback if provided
            if (payload['onSuccess']) payload.onSuccess();
        }
    } catch (e) { EventBus.publish('error', 'Failed to verify phone') }
}

// Watcher for phone actions
function* actionWatcher() {
    yield takeEvery('GET_ME', getMe);
    yield takeEvery('REFRESH_SUBSCRIPTION', refreshSubscription);
    yield takeEvery('GET_BRANDING', getBranding);
    yield takeEvery('ACCEPT_INVITE', acceptInvite);
    yield takeEvery('CREATE_MEMBER', createMember);
    yield takeEvery('UPDATE_MEMBER', updateMember);
    yield takeEvery('DELETE_MEMBER', deleteMember);
    yield takeEvery('RESEND_INVITE', resendInvite);
    yield takeEvery('UPDATE_PROFILE', updateProfile);
    yield takeEvery('GET_PENDING_INVITES', getPendingInvites);

    yield takeEvery('CREATE_WORKSPACE', createWorkspace);
    yield takeEvery('SWITCH_WORKSPACE', switchWorkspace);
    yield takeEvery('UPDATE_WORKSPACE', updateWorkspace);

    yield takeEvery('GET_API_KEYS', getApiKeys);
    yield takeEvery('CREATE_API_KEY', createApiKey);
    yield takeEvery('DELETE_API_KEY', deleteApiKey);

    yield takeEvery('SEND_PHONE_VERIFICATION', sendPhoneVerification);
    yield takeEvery('VERIFY_PHONE_CODE', verifyPhoneCode);
}

// Root saga for phone actions
export default function* rootSaga() { yield all([actionWatcher()]) }

// Helper functions for API calls
function postCall({ path, payload }) {
    return axios.post(path, payload).then(response => ({ response })).catch(error => {
        if (error['response']['status'] === 401) EventBus.publish('tokenExpired');
        return { error };
    })
}

function getCall(path) {
    return axios.get(path).then(response => ({ response })).catch(error => {
        if (error['response']['status'] === 401) EventBus.publish('tokenExpired');
        return { error };
    })
}

function deleteCall(path) {
    return axios.delete(path).then(response => ({ response })).catch(error => {
        if (error['response']['status'] === 401) EventBus.publish('tokenExpired');
        return { error };
    })
}

function putCall({ path, payload, headers = {} }) {
    return axios.put(path, payload, { headers }).then(response => ({ response })).catch(error => {
        if (error['response']['status'] === 401) EventBus.publish('tokenExpired');
        return { error };
    })
}